import { useState, useContext } from 'react';
import {
  Box, Button, Typography, Paper, IconButton, Tooltip, Stack,
  Chip, CircularProgress, List, ListItem, ListItemText,
  ListItemIcon, ListItemSecondaryAction, Divider,
} from '@mui/material';
import {
  DeleteOutlined as DeleteIcon, Download as DownloadIcon,
  EditOutlined as EditIcon, InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon, Description as DocIcon,
  Settings as SettingsIcon, ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon, Add as AddIcon,
} from '@mui/icons-material';
import FormBuilder from './FormBuilder';
import { DataContext } from '../contexts/DataProvider';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import AgreementEditDialog from './AgreementEditDialog';
import AgreementUploadDialog from './AgreementUploadDialog';
import { useAgreements } from '../hooks/useAgreements';
import { useAgreementUpload } from '../hooks/useAgreementUpload';
import type { Agreement } from '../hooks/useAgreements';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso: string | undefined | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getFileExtension = (name: string) => name.split('.').pop()?.toLowerCase() || '';
const isEditable = (ext: string) => ext === 'doc' || ext === 'docx';

const FileTypeIcon = ({ ext }: { ext: string }) => {
  if (ext === 'pdf') return <PdfIcon sx={{ color: '#e53935', fontSize: 22 }} />;
  if (isEditable(ext)) return <DocIcon sx={{ color: '#1976d2', fontSize: 22 }} />;
  return <FileIcon sx={{ color: '#757575', fontSize: 22 }} />;
};

// ─── Main ────────────────────────────────────────────────────────────────────

interface AgreementsPageProps {
  selectedOrganization?: string;
}

const AgreementsPage = ({ selectedOrganization }: AgreementsPageProps) => {
  const { login }: any = useContext(DataContext);
  const [editTarget, setEditTarget] = useState<Agreement | null>(null);

  const {
    agreements, isLoading, selectedAgreement, setSelectedAgreement,
    deleteTarget, setDeleteTarget, configureTarget, setConfigureTarget,
    isMarkingReady, refetch, handleDelete, handleMarkReady, handleDownload,
  } = useAgreements(selectedOrganization);

  const {
    isUploading, uploadProgress, isDragOver, stagedFile, setStagedFile,
    templateName, setTemplateName, uploadModalOpen, setUploadModalOpen,
    fileInputRef, uploadFile, handleDragOver, handleDragLeave, handleDrop,
    handleBrowse, resetUploadState,
  } = useAgreementUpload(
    selectedOrganization,
    (data) => setConfigureTarget(data),
    refetch,
  );

  // ── Configure Fields view ─────────────────────────────────────────────────
  if (configureTarget) {
    const adminEmail = login?.data?.email || login?.data?.username || '';
    const adminName = login?.data?.name || login?.data?.fullName || adminEmail;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0}
          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton size="small" onClick={() => setConfigureTarget(null)}><ArrowBackIcon /></IconButton>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ maxWidth: 400, fontSize: '0.9rem' }}>
                Configure Fields — {configureTarget.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Drag and drop signature, date, and name fields into position. When done, click Save &amp; Mark Ready.
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" color="success" disabled={isMarkingReady} onClick={handleMarkReady}
            startIcon={isMarkingReady ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, minWidth: 160 }}>
            {isMarkingReady ? 'Saving…' : 'Save & Mark Ready'}
          </Button>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <FormBuilder
            documentId={configureTarget.docusealSlug}
            recipients={[{ email: adminEmail, name: adminName }]}
            mode="configure"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, height: '100%', overflow: 'hidden', p: { xs: 1, md: 1.5 }, boxSizing: 'border-box' }}>
      {/* ── LEFT: File List ──────────────────────────────────── */}
      <Box sx={{ width: { xs: '100%', md: '360px' }, minWidth: { md: '320px' }, maxWidth: { md: '380px' }, display: 'flex', flexDirection: 'column', gap: 1.5, height: { xs: 'auto', md: '100%' }, overflow: 'hidden', flexShrink: 0 }}>
        <Box sx={{ flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>Documents</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setUploadModalOpen(true)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 2, py: 0.5 }}>
              Add Template
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
            Upload and manage agreement forms and templates
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ borderRadius: '12px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)' }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
              Uploaded Files ({agreements.length})
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 0.75, minHeight: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress size={28} /></Box>
            ) : agreements.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={3} gap={1}>
                <FileIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: '0.8rem' }}>
                  No agreements uploaded yet.<br />Upload your first file above.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {agreements.map((ag, idx) => {
                  const ext = getFileExtension(ag.fileName);
                  const isSelected = selectedAgreement?._id === ag._id;
                  return (
                    <Box key={ag._id}>
                      <ListItem onClick={() => setSelectedAgreement(ag)} sx={{
                        borderRadius: 2, mb: 0.5, cursor: 'pointer',
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        border: '1px solid', borderColor: isSelected ? 'primary.light' : 'rgba(0,0,0,0.12)',
                        '&:hover': { bgcolor: isSelected ? 'primary.50' : 'action.hover' },
                        pr: 12, py: 0.75,
                      }}>
                        <ListItemIcon sx={{ minWidth: 32 }}><FileTypeIcon ext={ext} /></ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 120, fontSize: '0.8rem' }}>{ag.fileName}</Typography>
                              {ag.docusealTemplateId && (
                                ag.isTemplateReady
                                  ? <Chip label="Ready" size="small" color="success" icon={<CheckCircleIcon />} sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }} />
                                  : <Chip label="Not Configured" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#e65100', border: '1px solid #ffb74d' }} />
                              )}
                            </Stack>
                          }
                          secondary={<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{formatFileSize(ag.fileSize)} · {formatDate(ag.createdAt || ag.uploadedAt)}</Typography>}
                        />
                        <ListItemSecondaryAction>
                          <Stack direction="row" spacing={0.25}>
                            <Tooltip title="Download"><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownload(ag); }}><DownloadIcon sx={{ fontSize: 17 }} color="primary" /></IconButton></Tooltip>
                            {ag.docusealTemplateId && (
                              <Tooltip title={ag.isTemplateReady ? 'Reconfigure Fields' : 'Configure Fields'}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setConfigureTarget(ag); }}><SettingsIcon sx={{ fontSize: 17 }} color="primary" /></IconButton>
                              </Tooltip>
                            )}
                            {isEditable(ext) && (
                              <Tooltip title="Edit (replace file)"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditTarget(ag); }}><EditIcon sx={{ fontSize: 17 }} color="primary" /></IconButton></Tooltip>
                            )}
                            <Tooltip title="Delete"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(ag); }}><DeleteIcon sx={{ fontSize: 17 }} color="error" /></IconButton></Tooltip>
                          </Stack>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {idx < agreements.length - 1 && <Divider sx={{ my: 0.2, opacity: 0.4 }} />}
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ── RIGHT: Preview ───────────────────────────────────── */}
      <Paper elevation={2} sx={{ flex: 1, borderRadius: '12px', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0}
          sx={{ px: 2, py: 1.2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            {selectedAgreement && <FileTypeIcon ext={getFileExtension(selectedAgreement.fileName)} />}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ maxWidth: 350, fontSize: '0.9rem' }}>
                {selectedAgreement?.fileName || 'Preview'}
              </Typography>
              {selectedAgreement && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                  {formatFileSize(selectedAgreement.fileSize)} · Uploaded {formatDate(selectedAgreement.createdAt || selectedAgreement.uploadedAt)}
                </Typography>
              )}
            </Box>
          </Stack>
          {selectedAgreement && (
            <Stack direction="row" spacing={1}>
              {selectedAgreement.docusealTemplateId && (
                <Tooltip title="Reconfigure Fields"><IconButton size="small" onClick={() => setConfigureTarget(selectedAgreement)}><SettingsIcon color="primary" /></IconButton></Tooltip>
              )}
              <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(selectedAgreement)}><DownloadIcon color="primary" /></IconButton></Tooltip>
              {isEditable(getFileExtension(selectedAgreement.fileName)) && (
                <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditTarget(selectedAgreement)} sx={{ borderRadius: 2 }}>Edit</Button>
              )}
            </Stack>
          )}
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', p: 1.5, boxSizing: 'border-box' }}>
          {selectedAgreement ? (
            <FormBuilder
              documentId={selectedAgreement.docusealSlug}
              recipients={[{ email: login?.data?.email || '', name: login?.data?.name || '' }]}
              mode="configure"
              previewMode
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary', gap: 2 }}>
              <FileIcon sx={{ fontSize: 64, opacity: 0.3 }} />
              <Typography variant="body1" fontWeight={500}>Select a file to preview</Typography>
              <Typography variant="body2" align="center" sx={{ maxWidth: 280 }}>Choose an agreement from the list on the left to view its contents here.</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* ── Dialogs ──────────────────────────────────────────── */}
      <ConfirmDeleteModal
        open={!!deleteTarget}
        entityType="agreement"
        entityName={deleteTarget?.fileName ?? ''}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      <AgreementEditDialog
        open={!!editTarget} agreement={editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={() => { refetch(); setEditTarget(null); }}
      />
      <AgreementUploadDialog
        open={uploadModalOpen}
        isUploading={isUploading} uploadProgress={uploadProgress} isDragOver={isDragOver}
        stagedFile={stagedFile} setStagedFile={setStagedFile}
        templateName={templateName} setTemplateName={setTemplateName}
        fileInputRef={fileInputRef}
        onClose={resetUploadState}
        onUpload={uploadFile}
        onDragOver={handleDragOver} onDragLeave={handleDragLeave}
        onDrop={handleDrop} onBrowse={handleBrowse}
      />
    </Box>
  );
};

export default AgreementsPage;
